import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container } from "reactstrap";
import { Grid, Edit, Plus, Trash2 } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { fetchBranches } from "../../utils/apiCalls";
import { getBlocks, deleteBlock, updateBranchBlockSettings } from "../../utils/pickupBlockApi";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader as RLoader } from "../reporting/ReportingShared";
import BlockModal from "./BlockModal";

const filterSelectStyle = {
  padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`,
  fontFamily: "inherit", outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 220,
};
const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "8px 16px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
};
const ghostBtn = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: C.textMed, padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const chip = {
  display: "inline-block", padding: "3px 9px", margin: "2px", borderRadius: 6, fontSize: 12,
  fontWeight: 600, background: C.bg, color: C.textMed, border: `1px solid ${C.border}`,
};

const PickupBlockSetup = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [selectionRequired, setSelectionRequired] = useState(true);
  const [loading, setLoading] = useState(false);

  const [blockModal, setBlockModal] = useState({ open: false, block: null });

  const loadBranches = useCallback(async () => {
    const res = await fetchBranches({}, false);
    let list = res?.data || [];
    if (selectedMerchant && selectedMerchant !== "all") {
      list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
    }
    setBranches(list);
  }, [selectedMerchant]);

  const loadBlocks = useCallback(async () => {
    if (!selectedBranch) {
      setBlocks([]);
      return;
    }
    const data = await getBlocks({ branchId: selectedBranch });
    setBlocks(data?.blocks || []);
    setSelectionRequired(data?.pickupBlockSelectionRequired ?? true);
  }, [selectedBranch]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    (async () => {
      if (!selectedBranch) {
        setBlocks([]);
        return;
      }
      setLoading(true);
      await loadBlocks();
      setLoading(false);
    })();
  }, [selectedBranch, loadBlocks]);

  const stats = useMemo(() => {
    const active = blocks.filter((b) => b.isActive).length;
    const slots = blocks.reduce((sum, b) => sum + (Array.isArray(b.labels) ? b.labels.length : 0), 0);
    return { total: blocks.length, active, slots };
  }, [blocks]);

  const handleToggleRequired = async () => {
    const next = !selectionRequired;
    const result = await updateBranchBlockSettings({
      branchId: Number(selectedBranch),
      pickupBlockSelectionRequired: next,
    });
    if (result) {
      setSelectionRequired(next);
      successToast("Branch setting updated");
    }
  };

  const handleDeactivate = async (block) => {
    if (!window.confirm(`Deactivate "${block.name}"? It will no longer appear on the KDS.`)) return;
    const result = await deleteBlock(block.id);
    if (result) {
      successToast("Block deactivated");
      loadBlocks();
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Pickup Blocks" parent="Pickup Blocks" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* Filter bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <select style={filterSelectStyle} value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <div style={{ flex: 1 }} />
            {selectedBranch && (
              <button style={coralBtn} onClick={() => setBlockModal({ open: true, block: null })}>
                <Plus size={15} /> Add Block
              </button>
            )}
          </div>

          {!selectedBranch ? (
            <RCard>
              <div style={{ textAlign: "center", color: C.textLight, padding: 30 }}>
                Select a branch to manage its pickup blocks.
              </div>
            </RCard>
          ) : loading ? (
            <RLoader />
          ) : (
            <>
              {/* KPI tiles */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                <Tile label="Blocks" value={stats.total} icon="🧱" gradient={`linear-gradient(135deg, ${C.coral}, #FF8A80)`} delay={0} />
                <Tile label="Active" value={stats.active} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
                <Tile label="Total Slots" value={stats.slots} icon="🔢" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={120} />
              </div>

              {/* Branch-level: require block selection on KDS */}
              <RCard>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: C.text, fontWeight: 600 }}>
                  <input type="checkbox" checked={selectionRequired} onChange={handleToggleRequired} style={{ margin: 0 }} />
                  Require staff to pick a block when marking a pickup order ready
                  <span style={{ fontWeight: 400, fontSize: 12, color: C.textLight }}>
                    (when off, the KDS shows a "Skip" option)
                  </span>
                </label>
              </RCard>

              {/* Blocks list */}
              {blocks.length === 0 ? (
                <RCard>
                  <div style={{ textAlign: "center", color: C.textLight, padding: 24 }}>
                    No pickup blocks yet. Add a block and define its slot numbers.
                  </div>
                </RCard>
              ) : (
                blocks.map((block) => (
                  <RCard key={block.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <Grid size={18} color={C.coral} />
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{block.name}</span>
                      <Badge color={block.isActive ? C.green : C.textMed} bg={block.isActive ? C.greenLight : C.bg}>
                        {block.isActive ? "Active" : "Inactive"}
                      </Badge>
                      <span style={{ fontSize: 12, color: C.textLight }}>
                        {Array.isArray(block.labels) ? block.labels.length : 0} slots
                      </span>
                      <div style={{ flex: 1 }} />
                      <button style={ghostBtn} onClick={() => setBlockModal({ open: true, block })}>
                        <Edit size={13} /> Edit
                      </button>
                      {block.isActive && (
                        <button style={ghostBtn} onClick={() => handleDeactivate(block)}>
                          <Trash2 size={13} /> Deactivate
                        </button>
                      )}
                    </div>
                    <div>
                      {(Array.isArray(block.labels) ? block.labels : []).map((lbl, i) => (
                        <span key={i} style={chip}>{lbl}</span>
                      ))}
                    </div>
                  </RCard>
                ))
              )}
            </>
          )}
        </div>
      </Container>

      <BlockModal
        isOpen={blockModal.open}
        toggle={() => setBlockModal({ open: false, block: null })}
        block={blockModal.block}
        branchId={selectedBranch}
        onSaved={loadBlocks}
      />
    </Fragment>
  );
};

export default PickupBlockSetup;
